import React from 'react';
import { format, parse, isValid } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { cn } from '@/lib/utils';

/**
 * Cross-browser date picker. Stores ISO `YYYY-MM-DD` in state (same format
 * the backend expects and the same format native <input type="date"> uses).
 *
 * Avoids Safari-specific problems with native date inputs:
 *  - No silent empty-value on partial typed input
 *  - No wheel-picker "value not committed on blur" bug
 *  - Same UI on iOS Safari, macOS Safari, Chrome, Firefox
 */
export const DatePicker = ({ value, onChange, placeholder = 'Pick a date', disabled = false, testId }) => {
  // Parse ISO string to Date object
  const dateValue = value ? parse(value, 'yyyy-MM-dd', new Date()) : null;
  const isValidDate = dateValue && isValid(dateValue);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'w-full h-10 justify-start text-left font-normal',
            !isValidDate && 'text-slate-400'
          )}
          data-testid={testId}
        >
          <CalendarIcon className="mr-2 h-4 w-4 text-slate-500" />
          {isValidDate ? format(dateValue, 'MMM d, yyyy') : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={isValidDate ? dateValue : undefined}
          onSelect={(d) => onChange(d ? format(d, 'yyyy-MM-dd') : '')}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

/**
 * Cross-browser time picker. Stores `HH:MM` (24-hour) string in state — same
 * format the backend expects and native <input type="time"> uses.
 *
 * Two selects (hour + minute in 5-min increments). No native picker → no
 * Safari wheel-picker "value not committed" bug, no format-mismatch silent
 * blanking on typed input.
 */
export const TimePicker = ({ value, onChange, disabled = false, testId }) => {
  const [h = '', m = ''] = (value || '').split(':');

  const set = (newH, newM) => {
    if (newH === '' || newM === '') {
      onChange('');
    } else {
      onChange(`${newH.padStart(2, '0')}:${newM.padStart(2, '0')}`);
    }
  };

  const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
  const minutes = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <Select value={h} onValueChange={(v) => set(v, m || '00')} disabled={disabled}>
        <SelectTrigger className="h-10"><SelectValue placeholder="HH" /></SelectTrigger>
        <SelectContent className="max-h-64">
          {hours.map((hr) => (
            <SelectItem key={hr} value={hr}>{hr}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-slate-400">:</span>
      <Select value={m} onValueChange={(v) => set(h || '00', v)} disabled={disabled}>
        <SelectTrigger className="h-10"><SelectValue placeholder="MM" /></SelectTrigger>
        <SelectContent className="max-h-64">
          {minutes.map((min) => (
            <SelectItem key={min} value={min}>{min}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
